import { useCallback, useMemo } from "react"

import {
  useMetricDirectory,
  type MetricDirectoryEntry,
} from "@/metrics/registry"
import type { MetricRecord } from "@/metrics/runtime"
import { useMetricsListRecordDataTransport } from "@/metrics/transport-data"
import { useTransportQueryUtils } from "@/transport"
import type { UseQueryResult } from "@tanstack/react-query"

import { mapTransportMetricsToRecords } from "../utils/transportMetrics"

type MetricsData = Record<string, MetricRecord>

type MetricsListResult<T> = Omit<
  UseQueryResult<unknown, unknown>,
  "data" | "isLoading"
> & {
  data: T | undefined
  isLoading: boolean
  metricDirectoryByManager: Record<string, MetricDirectoryEntry>
  metricIdAliases: Record<string, string>
  unavailableMetricIds: string[]
  failedMetricIds: string[]
}

interface UseMetricsListOptions<T> {
  enabled?: boolean
  metricNames?: string[]
  referencedMetricIds?: string[]
  revalidateOnMount?: boolean | "stale"
  select?: (data: MetricsData) => T
  tagFilters?: string[]
}

export function useMetricsList<T = MetricsData>(
  options?: UseMetricsListOptions<T>,
): MetricsListResult<T> {
  const { select } = options ?? {}
  const query = useMetricsListRecordDataTransport({
    enabled: options?.enabled,
    metricNames: options?.metricNames,
    referencedMetricIds: options?.referencedMetricIds,
    revalidateOnMount: options?.revalidateOnMount,
    tagFilters: options?.tagFilters,
  })
  const { byManager } = useMetricDirectory()
  const metricRecords = useMemo(
    () =>
      query.data
        ? mapTransportMetricsToRecords(Object.values(query.data), byManager)
        : undefined,
    [query.data, byManager],
  )
  const selectedData = useMemo<T | undefined>(
    () =>
      metricRecords
        ? select
          ? select(metricRecords)
          : (metricRecords as T)
        : undefined,
    [metricRecords, select],
  )

  return {
    ...query,
    metricDirectoryByManager: byManager,
    data: selectedData,
    isLoading: query.isLoading,
  }
}

/**
 * Revalidates the metric catalog for a change this tab's query cache cannot
 * see: a creation form opened in another tab invalidates that tab's cache, not
 * this one, and the catalog refetches on neither mount nor focus.
 */
export function useRevalidateMetricsCatalog() {
  const transportQueryUtils = useTransportQueryUtils()

  return useCallback(
    () => void transportQueryUtils.invalidateOperation("listMetrics"),
    [transportQueryUtils],
  )
}
